/**
 * Fetch all gateway devices for the current organization (fleet view).
 * Joins with properties to get property names.
 * Selects only display-critical columns for fleet performance.
 */
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { GatewayDevice, LifecycleStatus, OperationalStatus } from '@/types/gateway';

export interface FleetGatewayDevice extends Pick<
  GatewayDevice,
  | 'id'
  | 'property_id'
  | 'profile_id'
  | 'lifecycle_status'
  | 'operational_status'
  | 'firmware_version'
  | 'wifi_rssi'
  | 'last_heartbeat_at'
  | 'bus_devices_expected'
  | 'bus_devices_responding'
  | 'writes_enabled'
  | 'ota_channel'
> {
  property_name: string | null;
  mac_address: string | null;
}

export function useGatewayDevices() {
  const { userProfile, loading: appLoading } = useApp();
  const orgId = userProfile?.org_id ?? null;

  const query = useQuery({
    queryKey: ['gateway-devices-fleet', orgId],
    queryFn: async (): Promise<FleetGatewayDevice[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('gateway_devices')
        .select(`
          id,
          property_id,
          profile_id,
          mac_address,
          lifecycle_status,
          operational_status,
          firmware_version,
          wifi_rssi,
          last_heartbeat_at,
          bus_devices_expected,
          bus_devices_responding,
          writes_enabled,
          ota_channel,
          properties!gateway_devices_property_id_fkey ( name )
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);

      return (data ?? []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        property_id: row.property_id as string | null,
        profile_id: row.profile_id as string | null,
        mac_address: row.mac_address as string | null,
        lifecycle_status: row.lifecycle_status as LifecycleStatus,
        operational_status: row.operational_status as OperationalStatus,
        firmware_version: row.firmware_version as string | null,
        wifi_rssi: row.wifi_rssi as number | null,
        last_heartbeat_at: row.last_heartbeat_at as string | null,
        bus_devices_expected: row.bus_devices_expected as number | null,
        bus_devices_responding: row.bus_devices_responding as number | null,
        writes_enabled: row.writes_enabled as boolean,
        ota_channel: row.ota_channel as string,
        property_name: (row.properties as { name: string } | null)?.name ?? null,
      }));
    },
    enabled: Boolean(orgId) && !appLoading,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  return {
    devices: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
